// Display formatting, from `Intl` only — zero bytes of dependency (TECH-STACK 3.6).
// Every rule here comes from 06-qol §11, which is a consistency contract across all
// 120 screens rather than a per-screen choice.

import { formatClock } from '@nest-recruit/shared';

/** 06-qol §3 and §11: an empty value renders as an em dash. Never blank, never "N/A",
 *  never "null". Also what a permission-restricted field renders as (§L23). */
export const EMPTY = '—';

/** Anything within this window reads as relative time; older reads as an absolute
 *  date (06-qol §11: "relative time for anything within a week"). */
const RELATIVE_WINDOW_DAYS = 7;

const MS_PER_MINUTE = 60_000;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;

/** Settled decision D5: the organisation operates in Asia/Dhaka; storage is UTC
 *  (CLAUDE.md non-negotiable #8). A per-user override arrives with users in S4, so
 *  every function here takes the zone as an argument and this is only the fallback. */
export const DEFAULT_TIME_ZONE = 'Asia/Dhaka';

export interface DateTimeOptions {
  /** IANA zone. Defaults to the organisation zone until per-user zones land in S4. */
  readonly timeZone?: string;
  readonly locale?: string;
}

const DEFAULT_LOCALE = 'en-GB';

type DatePattern = 'dd-mm-yyyy' | 'dd/mm/yyyy' | 'yyyy-mm-dd';

/**
 * S31 (ADMIN-15): the organisation's date pattern, zone and number style, set once at sign-in
 * and after every settings change. Module state rather than context so the hundreds of cells
 * that format a date never subscribe to anything.
 */
let display: { timeZone: string; datePattern: DatePattern; numberLocale: string } = {
  timeZone: DEFAULT_TIME_ZONE,
  datePattern: 'dd-mm-yyyy',
  numberLocale: DEFAULT_LOCALE,
};

export function setDisplayFormats(formats: { readonly timeZone: string; readonly datePattern: DatePattern; readonly numberLocale: string }): void {
  display = { ...formats };
}

/** The organisation's zone, for pickers that build a calendar day. */
export function displayTimeZone(): string {
  return display.timeZone;
}

function resolve(options: DateTimeOptions | undefined): {
  timeZone: string;
  locale: string;
} {
  return {
    timeZone: options?.timeZone ?? display.timeZone,
    locale: options?.locale ?? DEFAULT_LOCALE,
  };
}

function datePattern(day: string, month: string, year: string): string {
  if (display.datePattern === 'yyyy-mm-dd') return `${year}-${month}-${day}`;
  if (display.datePattern === 'dd/mm/yyyy') return `${day}/${month}/${year}`;
  return `${day}-${month}-${year}`;
}

/** `Intl` formatters are expensive to construct and are asked for the same few
 *  shapes thousands of times while a virtualized table scrolls. */
const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatter(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  const cached = formatterCache.get(key);
  if (cached) return cached;
  const created = new Intl.DateTimeFormat(locale, options);
  formatterCache.set(key, created);
  return created;
}

function part(parts: readonly Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string {
  return parts.find((p) => p.type === type)?.value ?? '';
}

function toDate(value: Date | string | number | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * `DD-MM-YYYY` (06-qol §11). Built from parts rather than a locale pattern, because
 * `en-GB` gives `DD/MM/YYYY` and the separator is specified, not incidental.
 */
export function formatDate(
  value: Date | string | number | null | undefined,
  options?: DateTimeOptions,
): string {
  const date = toDate(value);
  if (!date) return EMPTY;
  const { locale, timeZone } = resolve(options);
  const parts = formatter(locale, {
    timeZone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).formatToParts(date);
  return datePattern(part(parts, 'day'), part(parts, 'month'), part(parts, 'year'));
}

/**
 * `DD-MM-YYYY` for a **plain calendar date** — a `YYYY-MM-DD` string with no
 * instant behind it, like a holiday (S6).
 *
 * Kept apart from `formatDate` on purpose. Sending "2026-03-26" through `Date` makes
 * it midnight UTC, and rendering that in a timezone west of Greenwich shows the 25th.
 * A holiday is not a moment in time and must not be treated as one; the only correct
 * transformation is on the string itself.
 */
export function formatIsoDate(value: string | null | undefined): string {
  if (!value) return EMPTY;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return EMPTY;
  return datePattern(match[3] ?? '', match[2] ?? '', match[1] ?? '');
}

/** The wall clock and zone of an instant. `h23` rather than `hour12: false`, which some
 *  engines render as "24:00" at midnight; the am/pm comes from `formatClock` (D10). */
function clockParts(date: Date, options: DateTimeOptions | undefined): { clock: string; zone: string } {
  const { locale, timeZone } = resolve(options);
  const parts = formatter(locale, {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZoneName: 'short',
  }).formatToParts(date);
  return { clock: formatClock(`${part(parts, 'hour')}:${part(parts, 'minute')}`), zone: part(parts, 'timeZoneName') || timeZone };
}

/**
 * `3:45 pm TZ` — **the timezone abbreviation is mandatory on every time we ever show**
 * (06-qol §11, CLAUDE.md non-negotiable #8). A meeting time without a zone is how
 * somebody misses an interview. 12-hour with am/pm (product owner, 2026-10-25, D10).
 */
export function formatTime(
  value: Date | string | number | null | undefined,
  options?: DateTimeOptions,
): string {
  const date = toDate(value);
  if (!date) return EMPTY;
  const { clock, zone } = clockParts(date, options);
  return `${clock} ${zone}`;
}

/** `3:45 pm` without the zone — only where the zone is already on screen beside it
 *  ("3:45 pm – 4:30 pm GMT+6", a calendar headed "Times in GMT+6"). */
export function formatClockTime(
  value: Date | string | number | null | undefined,
  options?: DateTimeOptions,
): string {
  const date = toDate(value);
  if (!date) return EMPTY;
  return clockParts(date, options).clock;
}

/** The zone's abbreviation at that instant, e.g. `GMT+6` — for "Times in GMT+6". */
export function formatZoneName(value: Date | string | number, options?: DateTimeOptions): string {
  const date = toDate(value) ?? new Date();
  return clockParts(date, options).zone;
}

/** `DD-MM-YYYY 3:45 pm TZ`. The zone rides along, always. */
export function formatDateTime(
  value: Date | string | number | null | undefined,
  options?: DateTimeOptions,
): string {
  const date = toDate(value);
  if (!date) return EMPTY;
  return `${formatDate(date, options)} ${formatTime(date, options)}`;
}

/** The full absolute form, for the `title` attribute behind a relative time. */
export function formatDateTimeLong(
  value: Date | string | number | null | undefined,
  options?: DateTimeOptions,
): string {
  const date = toDate(value);
  if (!date) return EMPTY;
  const { locale, timeZone } = resolve(options);
  const parts = formatter(locale, {
    timeZone,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).formatToParts(date);
  const weekday = part(parts, 'weekday');
  const day = part(parts, 'day');
  const month = part(parts, 'month');
  const year = part(parts, 'year');
  return `${weekday} ${day} ${month} ${year}, ${formatTime(date, options)}`;
}

const relativeFormatterCache = new Map<string, Intl.RelativeTimeFormat>();

function relativeFormatter(locale: string): Intl.RelativeTimeFormat {
  const cached = relativeFormatterCache.get(locale);
  if (cached) return cached;
  const created = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  relativeFormatterCache.set(locale, created);
  return created;
}

/**
 * "2 days ago" inside a week, `DD-MM-YYYY` beyond it (06-qol §11). Pair it with
 * `formatDateTimeLong` in a `title` so the exact moment is always one hover away.
 *
 * `now` is a parameter rather than a `Date.now()` call so this stays pure and the
 * boundary cases (exactly 7 days, DST transitions) are testable.
 */
export function formatRelative(
  value: Date | string | number | null | undefined,
  options?: DateTimeOptions & { readonly now?: Date },
): string {
  const date = toDate(value);
  if (!date) return EMPTY;
  const { locale } = resolve(options);
  const now = options?.now ?? new Date();

  const diffMinutes = Math.round((date.getTime() - now.getTime()) / MS_PER_MINUTE);
  const absMinutes = Math.abs(diffMinutes);
  const rtf = relativeFormatter(locale);

  if (absMinutes < 1) return 'just now';
  if (absMinutes < MINUTES_PER_HOUR) return rtf.format(diffMinutes, 'minute');

  const diffHours = Math.round(diffMinutes / MINUTES_PER_HOUR);
  if (Math.abs(diffHours) < HOURS_PER_DAY) return rtf.format(diffHours, 'hour');

  const diffDays = Math.round(diffHours / HOURS_PER_DAY);
  if (Math.abs(diffDays) <= RELATIVE_WINDOW_DAYS) return rtf.format(diffDays, 'day');

  return formatDate(date, options);
}

/** Whole numbers with grouping, tabular in the UI via `--flock-font-data`. */
export function formatNumber(
  value: number | null | undefined,
  options?: { readonly locale?: string },
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EMPTY;
  return new Intl.NumberFormat(options?.locale ?? display.numberLocale).format(value);
}

/**
 * The one place a value becomes display text. Anything falsy-but-meaningful — `0`,
 * `false` — is preserved; only genuinely absent values become the em dash, because
 * a count of zero is data and a missing count is not.
 */
export function orEmpty(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return EMPTY;
  const text = String(value).trim();
  return text === '' ? EMPTY : text;
}

/**
 * Initials for an avatar. Two letters at most; falls back to the first character so
 * a single-word name still renders something rather than a blank circle.
 */
export function initialsOf(name: string): string {
  // Words that don't start with a letter or digit, such as "(you)", never give an initial.
  const words = name.trim().split(/\s+/).filter((word) => /^[\p{L}\p{N}]/u.test(word));
  if (words.length === 0) return '';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

/**
 * 06-qol §11: sentence case everywhere — never Title Case, never ALL CAPS. Applied to
 * text arriving from data (an enum label, an imported column heading), never to text
 * we wrote, which is already correct. Acronyms are left alone.
 */
export function toSentenceCase(text: string): string {
  const trimmed = text.trim();
  if (trimmed === '') return '';
  const isAcronym = trimmed.length <= 3 && trimmed === trimmed.toUpperCase();
  if (isAcronym) return trimmed;
  return trimmed[0].toUpperCase() + trimmed.slice(1).toLowerCase();
}

// A Bangladeshi mobile as it is stored (+880 then 10 digits, §L26) or typed (01…, 880…).
const BD_MOBILE = /^(?:\+?880|0)(1\d{3})(\d{6})$/;

/**
 * "+880 1711-000482" — a Bangladeshi mobile grouped for reading (Redesign Group 7). Display only:
 * the stored value stays +8801711000482. Anything else is shown as it is.
 */
export function formatPhone(value: string): string {
  const match = BD_MOBILE.exec(value.replace(/[\s-]/g, ''));
  return match ? `+880 ${match[1] ?? ''}-${match[2] ?? ''}` : value;
}
